<script setup>
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { accountScopedRoute } = useAccount();
const { t } = useI18n();

const modes = [
  {
    name: 'Shadow',
    icon: 'i-lucide-scan-search',
    description: t('ACTIVE_GAMES_HUB.AI.MODES.SHADOW'),
  },
  {
    name: 'Suggest',
    icon: 'i-ph-sparkle-fill',
    description: t('ACTIVE_GAMES_HUB.AI.MODES.SUGGEST'),
  },
  {
    name: 'Route',
    icon: 'i-lucide-route',
    description: t('ACTIVE_GAMES_HUB.AI.MODES.ROUTE'),
  },
];
</script>

<template>
  <main class="h-full overflow-y-auto bg-n-background p-6 lg:p-10">
    <div class="mx-auto flex max-w-5xl flex-col gap-8">
      <section
        class="overflow-hidden rounded-3xl bg-gradient-to-br from-n-iris-3 via-n-surface-1 to-n-teal-3 p-7 ring-1 ring-n-weak lg:p-10"
      >
        <div
          class="mb-6 flex size-16 items-center justify-center rounded-2xl bg-n-iris-9 text-white shadow-lg"
        >
          <Icon icon="i-woot-captain" class="size-9" />
        </div>
        <p
          class="mb-2 text-xs font-black uppercase tracking-[0.22em] text-n-iris-11"
        >
          {{ $t('ACTIVE_GAMES_HUB.AI.EYEBROW') }}
        </p>
        <h1 class="m-0 text-4xl font-black tracking-tight text-n-slate-12">
          {{ $t('ACTIVE_GAMES_HUB.AI.NAME') }}
        </h1>
        <p class="mt-4 max-w-3xl text-base leading-7 text-n-slate-11">
          {{ $t('ACTIVE_GAMES_HUB.AI.DESCRIPTION') }}
        </p>
        <div class="mt-6 flex flex-wrap gap-3">
          <span
            class="rounded-full bg-n-amber-3 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-n-amber-11"
          >
            {{ $t('ACTIVE_GAMES_HUB.AI.AWAITING_ACTIVATION') }}
          </span>
          <span
            class="rounded-full bg-n-teal-3 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-n-teal-11"
          >
            {{ $t('ACTIVE_GAMES_HUB.AI.NO_AUTONOMOUS_SEND') }}
          </span>
        </div>
      </section>

      <section>
        <div class="mb-4">
          <h2 class="m-0 text-2xl font-black text-n-slate-12">
            {{ $t('ACTIVE_GAMES_HUB.AI.ROLLOUT_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('ACTIVE_GAMES_HUB.AI.ROLLOUT_DESCRIPTION') }}
          </p>
        </div>
        <div class="grid gap-4 md:grid-cols-3">
          <article
            v-for="(mode, index) in modes"
            :key="mode.name"
            class="rounded-2xl bg-n-surface-1 p-5 ring-1 ring-n-weak"
          >
            <div class="mb-4 flex items-center justify-between">
              <span
                class="flex size-11 items-center justify-center rounded-xl bg-n-iris-3 text-n-iris-11"
              >
                <Icon :icon="mode.icon" class="size-5" />
              </span>
              <span class="text-xs font-black text-n-slate-9">
                {{ String(index + 1).padStart(2, '0') }}
              </span>
            </div>
            <h3 class="m-0 text-lg font-black text-n-slate-12">
              {{ mode.name }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-n-slate-11">
              {{ mode.description }}
            </p>
          </article>
        </div>
      </section>

      <section
        class="flex flex-col gap-4 rounded-2xl bg-n-surface-1 p-6 ring-1 ring-n-weak md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h2 class="m-0 text-lg font-black text-n-slate-12">
            {{ $t('ACTIVE_GAMES_HUB.AI.TRY_TITLE') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('ACTIVE_GAMES_HUB.AI.TRY_DESCRIPTION') }}
          </p>
        </div>
        <router-link :to="accountScopedRoute('home')">
          <Button
            :label="$t('ACTIVE_GAMES_HUB.AI.OPEN_INBOX')"
            icon="i-lucide-inbox"
          />
        </router-link>
      </section>
    </div>
  </main>
</template>
